import MobileAppBar from '@/components/molecules/MobileAppBar';
import DashboardTemplate from '@/components/templates/DashboardTemplate';
import React, { useEffect, useState } from 'react';
import editIcon from '@/assets/icons/edit.svg';
import chevronRightIcon from '@/assets/icons/chevron-right.svg';
import { Button } from '@/components/ui/button';
import { authService } from '@/services/auth.service';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import defaultAvatar from '@/assets/images/default-user.png';
import ProfileName from '@/components/molecules/ProfileName';
import ProfileInfoCard from '@/components/molecules/ProfileInfoCard';
import SubscriptionPlanCard from '@/components/molecules/SubscriptionPlanCard';
import InfoModal from '@/components/molecules/InfoModal';
import { useDeviceType } from '@/hooks/useDeviceType';
import logoutIcon from '@/assets/icons/logout.svg';
import deleteIcon from '@/assets/icons/delete-account.svg';
import unregisterUserIcon from '@/assets/icons/unregister-user.svg';
import { PAGE } from '@/routes';
import SidebarLink from '@/components/molecules/SidebarLink';
import logoutRedIcon from '@/assets/icons/logout-red.svg';
import UserProfilePicInput from '@/components/molecules/UserProfilePicInput';
import cameraIcon from '@/assets/icons/camera.svg';
import InputField from '@/components/atoms/InputField';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { userService } from '@/services/user.service';
import Cookies from 'js-cookie';
import { getCurrentUser, setUser } from '@/store/slices/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@/hooks/useAuth';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { calculateRemainingDays } from '@/utils/helpers';
import { useClerk } from '@clerk/clerk-react';
const editProfileSchema = z.object({
  firstName: z.string().min(2, 'Must be greater than 2').optional(),
  lastName: z.string().min(2, 'Must be greater than 2').optional(),
  username: z.string().optional(),
  email: z.string().email(),
  phone: z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, {
      message: 'Phone number must be in E.164 format: +[country code][number]'
    })
    .optional()
});

type ProfileFormData = z.infer<typeof editProfileSchema>;

const ProfilePage = () => {
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isUnregisterOpen, setIsUnregisterOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('profile');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isReactivateOpen, setIsReactivateOpen] = useState(false);

  const { isMobileView } = useDeviceType();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { signOut } = useClerk();
  const dispatch = useDispatch();
  const currentUser = useSelector(getCurrentUser);

  const userStatus = currentUser?.status;
  console.log('userStatus', userStatus);
  console.log('cUser', currentUser);

  const remainingDays = calculateRemainingDays(currentUser?.unregisterDate || '');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ProfileFormData>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      email: ''
    }
  });

  useEffect(() => {
    // Sets breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Profile',
          path: PAGE.PROFILE.path
        }
      ])
    );
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout(signOut);
      navigate('/login'); // redirecting to login page after logout
    } catch (error) {
      toast.error('Logout failed. Please try again');
    }
  };

  const handleDelete = async () => {
    try {
      console.log('currentUser', currentUser);
      if (currentUser) await userService.deleteProfile(currentUser?.id);
      handleLogout();
    } catch (error) {
      toast.error('Failed to delete profile. Please try again');
    }
  };

  const handleEditClick = () => {
    navigate(PAGE.PROFILE.children.EDIT);
  };

  const handleBillingHistoryClick = () => {
    navigate(PAGE.PROFILE.children.BILLING_HISTORY);
  };

  const handlePaymentMethodClick = () => {
    navigate(PAGE.PROFILE.children.PAYMENT_METHOD);
  };

  const handleTerminologyClick = () => {
    navigate(PAGE.PROFILE.children.TERMINOLOGY);
  };

  const handleNavigation = (path: string) => {
    setActiveLink(path);
    // navigate(`/${path}`);
  };

  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
  };

  const handleImageRemove = () => {
    setSelectedImage(null);
  };

  const handleUnregister = async () => {
    try {
      await userService.unregister();

      dispatch(
        setUser(
          currentUser && {
            ...currentUser,
            status: 'unregistered',
            unregisterDate: new Date().toISOString()
          }
        )
      );

      toast.success('Unregistered successfully');
      setIsUnregisterOpen(false);
    } catch (error) {
      toast.error('Failed to unregister. Please try again');
    }
  };

  const handleReactivateUser = async () => {
    try {
      await userService.reactivateUser();

      dispatch(
        setUser(
          currentUser && {
            ...currentUser,
            status: 'ACTIVE',
            unregisterDate: null
          }
        )
      );

      toast.success('Reactivated successfully');
      setIsReactivateOpen(false);
    } catch (error) {
      toast.error('Failed to reactivate. Please try again');
    }
  };

  return (
    <>
      <MobileAppBar title="Profile" variant="primary" onRightIconClick={handleEditClick} rightIcon={editIcon} />

      {isMobileView ? (
        <div className="p-5 py-4 flex-1 flex flex-col pb-8 gap-6">
          {['unregistered', 'UNREGISTERED'].includes(userStatus || '') && (
            <header className="p-4 border-b-2 border-primary bg-lightPurple rounded-[4px] mb-0">
              <p className="font-medium text-sm">You already unregistered</p>
              <p className="text-xs text-secondary leading-[100%]">
                You have {remainingDays} days remaining before your data being deleted
              </p>
            </header>
          )}

          {/* PROFILE NAME */}
          <ProfileName profilePic={currentUser?.photoURL || defaultAvatar} name={currentUser?.name || 'Guest User'} />

          {/* PROFILE INFO */}
          <ProfileInfoCard
            username={currentUser?.username || '-'}
            email={currentUser?.email || user?.emailAddresses?.[0]?.emailAddress || ''}
            phone={currentUser?.phoneNumber || '-'}
            hourlyRate={currentUser?.hourlyRate}
          />

          {/* SUBSCRIPTION INFO */}
          <SubscriptionPlanCard plan="basic" />

          {/* PAYMENT & BILLING */}
          <div className="p-[1.2rem] py-[0.3rem] bg-white border rounded-[16px]">
            {/* <button
              onClick={handlePaymentMethodClick}
              className="w-full flex items-center justify-between gap-4 border-b py-4"
            >
              <span className="font-bold text-sm">Payment Method</span>
              <img src={chevronRightIcon} alt="chevron-right" />
            </button>
            <button
              onClick={handleBillingHistoryClick}
              className="w-full flex items-center justify-between gap-4 border-b py-4"
            >
              <span className="font-bold text-sm">Billing History</span>
              <img src={chevronRightIcon} alt="chevron-right" />
            </button> */}
            <button onClick={handleTerminologyClick} className="w-full flex items-center justify-between gap-4 py-4">
              <span className="font-bold text-sm">Terminology</span>
              <img src={chevronRightIcon} alt="chevron-right" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-2">
            <Button
              onClick={() => {
                setIsLogoutOpen(true);
              }}
              type="button"
              variant={'primary-reverse'}
              className="w-full rounded-full text-base font-semibold"
            >
              Logout
            </Button>
            {['ACTIVE', 'active'].includes(userStatus || '') ? (
              <Button
                onClick={() => setIsUnregisterOpen(true)}
                variant={'text'}
                className="mx-auto w-fit text-[#E8507E] h-auto"
              >
                Unregister
              </Button>
            ) : ['unregistered', 'UNREGISTERED'].includes(userStatus || '') ? (
              <Button
                onClick={() => setIsReactivateOpen(true)}
                variant={'text'}
                className="mx-auto w-fit text-primary h-auto"
              >
                Cancel Unregister
              </Button>
            ) : (
              <></>
            )}
            <Button
              onClick={() => {
                setIsDeleteOpen(true);
              }}
              variant={'text'}
              className="mx-auto w-fit text-[#E8507E] h-auto"
            >
              Delete Account
            </Button>
          </div>
        </div>
      ) : (
        <div className="h-full flex flex-col justify-between">
          {['unregistered', 'UNREGISTERED'].includes(userStatus || '') && (
            <header className="p-4 border-b-2 border-primary bg-lightPurple rounded-[4px] mb-4">
              <p className="font-medium text-sm">You already unregistered</p>
              <p className="text-xs text-secondary leading-[100%]">
                You have {remainingDays} days remaining before your data being deleted
              </p>
            </header>
          )}
          <div className=" space-y-4">
            <div>
              <span className="inline-block text-sm font-medium text-gray mb-4">Profile Picture</span>
              <img
                className="rounded-full w-[120px] h-[120px] object-cover"
                src={currentUser?.photoURL || defaultAvatar}
                alt=""
                width={120}
                height={120}
              />
            </div>
            <div>
              <span className="text-sm font-medium text-gray">Username</span>
              <p className="text-base font-medium leading-[19.5px]">{currentUser?.username || '-'}</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray">First name</span>
                <p className="text-base font-medium leading-[19.5px]">{currentUser?.firstName || '-'}</p>
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium text-gray">Last name</span>
                <p className="text-base font-medium leading-[19.5px]">{currentUser?.lastName || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray">Mail</span>
                <p className="text-base font-medium leading-[19.5px]">
                  {currentUser?.email || user?.emailAddresses?.[0]?.emailAddress}
                </p>
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium text-gray">Hourly Rate</span>
                <p className="text-base font-medium leading-[19.5px]">{currentUser?.hourlyRate}€/ hour</p>
              </div>
              {/* <div className="flex-1">
                <span className="text-sm font-medium text-gray">Phone Number</span>
                <p className="text-base font-medium leading-[19.5px]">
                  {currentUser?.phoneNumber ? formatPhoneNumberIntl(currentUser?.phoneNumber) : '-'}
                </p>
              </div> */}
            </div>
          </div>
          <footer className="mt-auto mb-3 flex items-center justify-between">
            <Button
              onClick={() => setIsLogoutOpen(true)}
              variant={'text'}
              className="h-0 m-0 p-0 text-lightRed font-semibold gap-2"
            >
              <img src={logoutRedIcon} alt="" />
              <span>Logout</span>
            </Button>
            <div className="space-x-5">
              <Button
                onClick={() => setIsDeleteOpen(true)}
                variant={'text'}
                className="h-0 m-0 p-0 text-base text-lightRed font-semibold gap-2"
              >
                Delete Account
              </Button>
              {['ACTIVE', 'active'].includes(userStatus || '') ? (
                <Button
                  onClick={() => setIsUnregisterOpen(true)}
                  variant={'text'}
                  className="h-0 m-0 p-0 text-base text-lightRed font-semibold gap-2"
                >
                  Unregister
                </Button>
              ) : ['unregistered', 'UNREGISTERED'].includes(userStatus || '') ? (
                <Button
                  onClick={() => setIsReactivateOpen(true)}
                  variant={'text'}
                  className="h-0 m-0 p-0 text-base text-primary font-semibold gap-2"
                >
                  Cancel Unregister
                </Button>
              ) : (
                <></>
              )}
              <Button
                onClick={handleEditClick}
                variant={'primary-reverse'}
                className="text-base px-5 rounded-full font-semibold gap-2"
              >
                Edit Profile
              </Button>
            </div>
          </footer>
        </div>
      )}
      {isDeleteOpen && (
        <InfoModal
          isOpen={isDeleteOpen}
          variant="confirm"
          title="Are you sure want to Delete this Account?"
          message="All Budget will removed permanently and unable to restore. You’ll automatically signed out"
          buttonText="Delete"
          onButtonClick={handleDelete}
          icon={deleteIcon}
          onClose={() => {
            setIsDeleteOpen(false);
          }}
        />
      )}
      {isLogoutOpen && (
        <InfoModal
          isOpen={isLogoutOpen}
          variant="confirm"
          title="Are you sure want to Logout?"
          message="You’ll automatically signed out from BatJet"
          buttonText="Logout"
          onButtonClick={handleLogout}
          icon={logoutIcon}
          onClose={() => {
            setIsLogoutOpen(false);
          }}
        />
      )}
      {isUnregisterOpen && (
        <InfoModal
          isOpen={isUnregisterOpen}
          variant="confirm"
          title="Are you sure want to Unregister?"
          message="All your data will be deleted on 30 days after unregister"
          buttonText="Unregister"
          onButtonClick={handleUnregister}
          icon={unregisterUserIcon}
          onClose={() => {
            setIsUnregisterOpen(false);
          }}
        />
      )}
      {isReactivateOpen && (
        <InfoModal
          isOpen={isReactivateOpen}
          variant="confirm"
          title="Are you sure want to Reactivate?"
          message="You’ll automatically signed in"
          buttonText="Reactivate"
          onButtonClick={handleReactivateUser}
          icon={unregisterUserIcon}
          onClose={() => {
            setIsReactivateOpen(false);
          }}
        />
      )}
    </>
  );
};

export default ProfilePage;
